define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/aspect",
    "mw-log/Log",
    "mw-utils/Utils",
    "mw-messageservice/MessageService",
    "./ImageDifferencer",
    "./PagedData",
    "./Serializers"
], function (declare, lang, aspect, Log, Utils, MessageService, ImageDifferencer, PagedData,
             Serializers) {

    //noinspection JSUnusedGlobalSymbols
    var PeerSynchronizer;
    PeerSynchronizer = declare(null, {

        _MAX_RETRIES: 3,

        constructor: function (namespace, manager, messageService, isClient, clientId) {
            this.namespace = namespace;
            this.manager = manager;

            this.messageService = messageService || MessageService.messageService;
            Serializers.setupSerializers(this.messageService);

            this._originator = {};

            this.messageCount = 0;
            this.syncEnabled = {};

            this.isClient = isClient === undefined ? true : !!isClient;
            this.clientId = typeof clientId === "string" ? clientId : Utils.generateUuid();

            this.imageDifferencer = new ImageDifferencer(this._originator);

            this._queue = [];

            this._onConnectedHandler = aspect.after(this.messageService, "onConnected", lang.hitch(
                this, this._handleConnected));
            this._onNotConnectedHandler = aspect.after(this.messageService,
                "onNotConnected", lang.hitch(this, this._handleNotConnected));
            if (!this.messageService.isConnected()) {
                this._handleNotConnected();
            } else {
                this._handleConnected();
            }


            try {
                this.addManagerListeners();
                this.addMessageSubscriptions();
            } catch (e) {
                Log.error("Not able to create peer synchronizer: " + e);
            }
        },


        remove: function () {
            this._handleNotConnected();
            this.removeMessageSubscriptions();
            this.removeManagerListeners();
            this._onConnectedHandler.remove();
            this._onNotConnectedHandler.remove();
        },


        _handleNotConnected: function () {
            this.log("Not connected");
            this._connected = false;
            this._handshook = false;
        },

        _handleConnected: function () {
            this.log("Connected");
            this._connected = true;
            this._startHandshake();
        },

        _startHandshake: function () {
            this.log("Starting handshake");
            var data = this.createNewMessage();
            data.clientId = this.clientId;
            data.syncEnabled = this.manager.isSyncEnabled();
            this.publish(this.getChannel("getStatus"), data);
        },

        _handshakeComplete: function () {
            this.log("Handshake complete");
            this._handshook = true;
            this._processQueue();
        },

        _processQueue: function () {
            var queue = this._queue;
            this._queue = [];
            this.log("Processing queue", queue);
            queue.forEach(function (op) {
                this.publish(op.channel, op.message);
            }, this);
        },

        shouldSync: function (event) {
            return this.manager.isSyncEnabled() && (!event ||
                event.originator !== this._originator);
        },

        isReady: function () {
            return this._connected && this._handshook;
        },


        syncChangedObserver: function () {
            this.syncEnabled[this.clientId] = this.manager.isSyncEnabled();

            if (this.manager.isSyncEnabled()) {
                this.addSyncMessageSubscriptions();
                this.addSyncManagerListeners();

                if (this._connected) {
                    this._startHandshake();
                }
            } else {
                this.removeSyncMessageSubscriptions();
                this.removeSyncManagerListeners();

                this._handshook = false;
            }
        },

        getStatusSubscriber: function (message) {
            this.log("getStatusSubscriber", message);
            var data = this.createNewMessage();
            data.syncEnabled = this.manager.isSyncEnabled();
            data.clientId = this.clientId;
            this.publish(this.getChannel("status"), data);

            this.statusSubscriber(message);
        },

        statusSubscriber: function (message) {
            this.log("Status received", message);
            this.syncEnabled[message.data.clientId] = message.data.syncEnabled;
            if (this.isClient && this.manager.isSyncEnabled()) {
                this.publish(this.getChannel("getFullUpdate"), this.createNewMessage());
            }
        },


        getFullUpdateSubscriber: function () {
            this.log("getFullUpdate requested");
            if (this.manager.isSyncEnabled()) {
                var data = this.createNewMessage();
                if (this.manager.hasRoot()) {
                    data.root = this.convertNodesRecursive(this.manager.getRoot(), 0);
                }
                this.publish(this.getChannel("fullUpdate"), data);

                this._handshakeComplete();
            }
        },

        fullUpdateSubscriber: function (message) {
            this.log("fullUpdate received", message);
            if (this.shouldSync()) {
                try {
                    var root = message.data.root;
                    if (root) {
                        // If there is an existing root which has a different id or type, destroy it
                        if (this.manager.hasRoot() &&
                                (root.id !== this.manager.getRoot().getId() ||
                                root.type !== this.manager.getRoot().getType())) {

                            this.manager.getRoot().destroy(this._originator);
                        }

                        this._handshakeComplete();

                        // Create the root node if necessary
                        if (!this.manager.hasRoot()) {
                            this.manager.setRootWithId(
                                root.id, root.type, root.properties, this._originator);
                        }

                        // Perform a differential update starting at the root node.
                        this.updateNodeRecursive(this.manager.getRoot(), root);

                    } else if (this.manager.hasRoot()) {
                        this.manager.getRoot().destroy(this._originator);
                        this._handshakeComplete();
                    } else {
                        this._handshakeComplete();
                    }

                } catch (e) {
                    this._error("Error while trying to do a full update.", message, e);
                }
            }
        },


        rootSetObserver: function (event) {
            this.addNodeListeners(event.target);

            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.target = this.convertNode(event.target);
                this.publish(this.getChannel(event.type), message);
            }
        },

        rootSetSubscriber: function (message) {
            this.log("rootSetSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var root = message.data.target;
                    if (this.manager.hasRoot()) {
                        this.manager.getRoot().destroy(this._originator);
                    }

                    var rootNode = this.manager.setRootWithId(
                        root.id, root.type, root.properties, this._originator);
                    // Update root the same way other nodes are updated
                    this.updateNodeRecursive(rootNode, root);
                } catch (e) {
                    this._error("Error setting new root.", message, e);
                }
            }
        },


        rootDestroyedObserver: function (event) {
            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.targetId = event.target.getId();
                this.publish(this.getChannel(event.type), message);
            }
        },

        rootDestroyedSubscriber: function (message) {
            this.log("rootDestroyedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    if (this.manager.hasRoot() &&
                            this.manager.getRoot().getId() === message.data.targetId) {

                        this.manager.getRoot().destroy(this._originator);
                    } else {
                        this._error("Tried to unset the wrong root.", message);
                    }
                } catch (e) {
                    this._error("Error destroying root.", message, e);
                }
            }
        },

        childMovedObserver: function (event) {
            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.targetId = event.target.getId();
                message.oldParentId = event.data.oldParent.getId();
                message.oldIndex = event.data.oldIndex;
                message.newParentId = event.data.newParent.getId();
                message.newIndex = event.data.newIndex;
                this.publish(this.getChannel(event.type), message);
            }
        },

        childMovedSubscriber: function (message) {
            this.log("childMovedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    var child = this.manager.getById(data.targetId);
                    var newParent = this.manager.getById(data.newParentId);
                    this.manager.move(child, newParent, data.newIndex, this._originator);

                } catch (e) {
                    this._error("Error while trying to move.", message, e);
                }
            }
        },


        childAddedObserver: function (event) {
            var child = event.data.child;
            this.addNodeListeners(child);

            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.parentId = event.target.getId();
                message.child = this.convertNode(child);
                message.index = event.data.index;
                this.publish(this.getChannel("subTreeAdded"), message);
            }
        },

        subTreeAddedSubscriber: function (message) {
            this.log("subTreeAddedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var child = message.data.child;
                    var node = this.manager.getById(message.data.parentId);
                    var childNode = node.addChildWithId(child.id, child.type, child.properties,
                            message.data.index, this._originator);
                    this.updateNodeRecursive(childNode, child);

                } catch (e) {
                    this._error("Error while trying to add a subtree of nodes.", message, e);
                }
            }
        },


        childDetachedObserver: function (event) {
            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.childId = event.data.child.getId();
                this.publish(this.getChannel(event.type), message);
            }
        },

        childDetachedSubscriber: function (message) {
            this.log("childDetachedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    this.manager.getById(message.data.childId).detach(this._originator);

                } catch (e) {
                    this._error("Error while trying to detach.", message, e);
                }
            }
        },


        childReattachedObserver: function (event) {
            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.childId = event.data.child.getId();
                message.index = event.data.index;
                this.publish(this.getChannel(event.type), message);
            }
        },

        childReattachedSubscriber: function (message) {
            this.log("childReattachedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    this.manager.getById(data.childId).reattach(data.index, this._originator);

                } catch (e) {
                    this._error("Error while trying to reattach.", message, e);
                }
            }
        },


        subTreeDestroyedObserver: function (event) {
            var child = event.data.child;
            this.removeNodeListenersRecursive(child);

            if (this.shouldSync(event) && this.isReady()) {
                var message = this.createNewMessage();
                message.childId = child.getId();
                this.publish(this.getChannel(event.type), message);
            }
        },

        subTreeDestroyedSubscriber: function (message) {
            this.log("subTreeDestroyedSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    var targetId = data.childId;
                    this.manager.getById(targetId).destroy(this._originator);

                } catch (e) {
                    this._error("Error while trying to destroy subtree.", message, e);
                }
            }
        },


        propertiesSetObserver: function (event) {
            if (this.shouldSync(event) && this.isReady() && !this.isClient) {
                var imageKeys = [];
                var message = this.createNewMessage();
                var key;
                message.targetId = event.target.getId();

                var newValues = event.data.newValues;
                var oldValues = event.data.oldValues;

                for (key in newValues) {
                    if (newValues.hasOwnProperty(key)) {
                        if (Utils.isImageData(newValues[key])) {
                            var oldValue = oldValues[key];
                            if (!!oldValue && Utils.isImageData(oldValue)) {
                                imageKeys.push(key);
                                var imageUpdateMessage = this.createNewMessage();
                                imageUpdateMessage.targetId = event.target.getId();
                                imageUpdateMessage.key = key;
                                imageUpdateMessage.newValue =
                                    this.imageDifferencer.computeImageDataDifferences(
                                        oldValue, newValues[key]);
                                this.publish(this.getChannel("imageUpdate"), message);
                            }
                        }
                    }
                }

                imageKeys.forEach(function (key) {
                    delete event.data.newValues[key];
                });
                message.properties = event.data.newValues;
                this.publish(this.getChannel(event.type), message);
            }
        },

        propertiesSetSubscriber: function (message) {
            this.log("propertiesSetSubscriber", message);
            if (this.shouldSync() || this.isClient) {
                try {
                    var data = message.data;
                    var targetId = data.targetId;
                    var node = this.manager.getById(targetId);
                    var key;
                    for (key in data.properties) {
                        if (data.properties.hasOwnProperty(key)) {
                            if (data.properties[key] instanceof Image) {
                                this.imageDifferencer.updatePropertyWithImage(
                                    node, key, data.properties[key]);
                            } else {
                                node.setProperty(key, data.properties[key], this._originator);
                                this._updateNodePagedData(node);
                            }
                        }
                    }

                } catch (e) {
                    this._error("Error while trying to update properties.", message, e);
                }
            }
        },

        imageUpdateSubscriber: function (message) {
            this.log("imageUpdateSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    var targetId = data.targetId;
                    var node = this.manager.getById(targetId);
                    var diffImages = data.newValue;
                    var key = data.key;
                    this.imageDifferencer.applyImageDifferences(node, key, diffImages);

                } catch (e) {
                    this._error("Error while trying to update images.", message, e);
                }
            }
        },


        propertiesUnsetObserver: function (event) {
            if (this.shouldSync(event) && this.isReady()) {
                var key;
                var message = this.createNewMessage();
                message.targetId = event.target.getId();
                message.keys = [];

                for (key in event.data.oldValues) {
                    if (event.data.oldValues.hasOwnProperty(key)) {
                        message.keys.push(key);
                    }
                }
                this.publish(this.getChannel(event.type), message);
            }
        },

        propertiesUnsetSubscriber: function (message) {
            this.log("propertiesUnsetSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    var targetId = data.targetId;
                    var node = this.manager.getById(targetId);
                    node.unsetProperties(data.keys, this._originator);

                } catch (e) {
                    this._error("Error while trying to unset properties.", message, e);
                }
            }
        },


        peerEventObserver: function (event) {
            var message = this.createNewMessage();
            message.targetId = event.target.getId();
            message.data = event.data;

            var channel = this.getChannel(event.type);

            if (this.shouldSync(event)) {
                if (this.isReady()) {
                    this.publish(channel, message);
                } else {
                    this._queue.push({channel: channel, message: message});
                }
            }
        },

        peerEventSubscriber: function (message) {
            this.log("peerEventSubscriber", message);
            if (this.shouldSync()) {
                try {
                    var data = message.data;
                    var targetId = data.targetId;
                    var node = this.manager.getById(targetId);
                    node.dispatchEvent("peerEvent", node, data.data, this._originator);

                } catch (e) {
                    this._error("Error while trying dispatch a peer event.", message, e);
                }
            }
        },



        publish: function (channel, message, retryCount) {
            this.log("Publishing message", channel, message);
            var future = this.messageService.publish(channel, message);
            var that = this;
            retryCount = retryCount === undefined ? this._MAX_RETRIES : retryCount;
            future.then(undefined, function () {
                if (retryCount > 0) {
                    Log.warn("Error sending message (will retry " + retryCount + "): ",
                        channel, message);
                    that.publish(channel, message, retryCount - 1);
                } else {
                    that._error("Error sending message, retry didn't work.", channel, message);
                }
            });
        },

        getChannel: function (event) {
            return "/peermodel" + this.namespace + "/" + event;
        },

        createNewMessage: function () {
            this.messageCount += 1;
            return {message: this.messageCount};
        },


        log: function () {
            if (this.namespace === PeerSynchronizer.debugNamespace) {
                var args = Array.prototype.slice.call(arguments);
                args = ["PeerSynchronizer (" + this.namespace + ")"].concat(args);
                Log.info.apply(Log, args);
            }
        },


        convertNodesRecursive: function (node, index) {
            var data = this.convertNode(node, index);
            var children = [];
            var nodeChildren = node.getChildren();
            var i;
            for (i = 0; i < nodeChildren.length; i += 1) {
                children.push(this.convertNodesRecursive(nodeChildren[i], i));
            }
            data.children = children;
            return data;
        },

        convertNode: function (node, index) {
            index = index || 0;
            return {
                id: node.getId(),
                type: node.getType(),
                index: index,
                properties: node.getProperties()
            };
        },

        /**
         * Differential node update from the server. The process is as follows:
         * 1) Update the properties of the node.
         * 2) Loop over old children, destroy ones which aren't in the new list.
         * 3) Loop over new children, add nodes which aren't in the old list.
         * 4) Loop over new children, move nodes which are in the wrong position.
         * 5) Loop over children and recursively update them.
         * @param node The existing node (may contain children).
         * @param data The new node (and it's children).
         */
        updateNodeRecursive: function (node, data) {
            var newProps, properties, keys, i, len;
            var nodes = node ? [node] : [];
            var datas = data ? [data] : [];


            while (datas.length > 0) {
                node = nodes.pop();
                data = datas.pop();

                newProps = this._getFilteredPropertyKeys(data.properties);
                // Do this only the first time. This is a special case. g982636
                if (!node._handled) {
                    node._handled = true;
                    properties = node.getProperties();
                    keys = Object.keys(properties);
                    len = keys.length;
                    for (i = 0; i < len; i += 1) {
                        newProps.filtered[keys[i]] = properties[keys[i]];
                    }
                }
                node.replaceProperties(newProps.filtered, this._originator);

                this._updateNodePagedData(node, properties);
                this._updateNodeImageProperties(node, data.properties, newProps.images);

                this._destroyOldChildren(node, data);
                this._addNewChildren(node, data);
                this._updateChildIndices(node, data);

                nodes = nodes.concat(node.getChildren());
                if (data.children) {
                    datas = datas.concat(data.children);
                }
            }
        },

        _destroyOldChildren: function (node, data) {
            var i;
            var children = node.getChildren();
            for (i = 0; i < children.length; i += 1) {
                if (!this._dataHasChild(data, children[i])) {
                    children[i].destroy(this._originator);
                }
            }
        },

        _addNewChildren: function (node, data) {
            var i, child, newProps;
            if (data.children) {
                for (i = 0; i < data.children.length; i += 1) {
                    child = data.children[i];
                    if (!node.hasChild(child.id)) {
                        newProps = this._getFilteredPropertyKeys(child.properties);
                        node.addChildWithId(
                            child.id, child.type, newProps.filtered, i, this._originator);
                    }
                }
            }
        },

        _updateChildIndices: function (node, data) {
            var i, child, childNode;
            if (data.children) {
                for (i = 0; i < data.children.length; i += 1) {
                    child = data.children[i];
                    childNode = node.getChild(child.id);
                    if (node.getChildIndex(childNode) !== i) {
                        this.manager.move(childNode, node, i, this._originator);
                    }
                }
            }
        },

        _dataHasChild: function (data, childNode) {
            var i;
            if (data.children) {
                for (i = 0; i < data.children.length; i += 1) {
                    if (data.children[i].id === childNode.getId() &&
                        data.children[i].type === childNode.getType()) {
                        return true;
                    }
                }
            }
            return false;
        },


        _error: function (description, message, error) {
            Log.error(description, message, error);
            if (this.manager.isSyncEnabled() && this.isClient) {
                // force a full update if there is an error while doing certain operations
                this.publish(this.getChannel("getFullUpdate"), this.createNewMessage());
            }
        },


        _getFilteredPropertyKeys: function (properties) {
            var filteredProperties = {};
            var imageProperties = [];
            var key;
            for (key in properties) {
                //noinspection JSUnfilteredForInLoop
                if (properties.hasOwnProperty(key) && properties[key] !== null &&
                        properties[key] !== undefined) {

                    //noinspection JSUnfilteredForInLoop
                    if (properties[key] instanceof Image) {
                        //noinspection JSUnfilteredForInLoop
                        imageProperties.push(key);
                    } else {
                        //noinspection JSUnfilteredForInLoop
                        filteredProperties[key] = properties[key];
                    }
                }
            }
            return {filtered: filteredProperties, images: imageProperties};
        },

        _updateNodePagedData: function (node, properties) {
            var key;

            if (!properties) {
                properties = node.getProperties();
            }

            for (key in properties) {
                if (properties.hasOwnProperty(key) &&
                        properties[key] instanceof PagedData) {

                    properties[key].setPeerNode(node, key);
                }
            }
        },

        _updateNodeImageProperties: function (node, properties, images) {
            var j;
            for (j = 0; j < images.length; j += 1) {
                this.imageDifferencer.updatePropertyWithImage(
                    node, images[j], properties[images[j]]);
            }
        },

        addMessageSubscriptions: function () {
            if (!this._messageSubscribed) {
                this._onConnectedSub = aspect.after(this.messageService, "onReconnected",
                    lang.hitch(this, "syncChangedObserver"));

                this.messageService.subscribe(this.getChannel("getStatus"),
                    "getStatusSubscriber", this);
                this._messageSubscribed = true;
            }

            if (this.manager.isSyncEnabled()) {
                this.addSyncMessageSubscriptions();
            }
        },

        removeMessageSubscriptions: function () {
            if (this._messageSubscribed) {
                this._onConnectedSub.remove();

                this.messageService.unsubscribe(this.getChannel("getStatus"),
                    "getStatusSubscriber", this);
                this._messageSubscribed = false;
            }
            this.removeSyncMessageSubscriptions();
        },

        syncMessageChannels: ["status", "getFullUpdate", "fullUpdate", "rootSet", "rootDestroyed",
                "childMoved", "childDetached", "childReattached", "subTreeAdded",
                "subTreeDestroyed", "propertiesSet", "propertiesUnset", "imageUpdate", "peerEvent"],

        addSyncMessageSubscriptions: function () {
            if (!this._syncMessageSubscribed) {
                this.log("addSyncMessageSubscriptions");
                this.syncMessageChannels.forEach(function (channelId) {
                    this.messageService.subscribe(
                        this.getChannel(channelId), channelId + "Subscriber", this);
                }, this);
                this._syncMessageSubscribed = true;
            }
        },

        removeSyncMessageSubscriptions: function () {
            if (this._syncMessageSubscribed) {
                this.log("removeSyncMessageSubscriptions");
                this.syncMessageChannels.forEach(function (channelId) {
                    this.messageService.unsubscribe(
                        this.getChannel(channelId), channelId + "Subscriber", this);
                }, this);
                this._syncMessageSubscribed = false;
            }
        },

        addManagerListeners: function () {
            if (!this._managerListeners) {
                this.manager.addSyncEventListener("syncChanged", "syncChangedObserver", this);
                this._managerListeners = true;
            }
            if (this.manager.isSyncEnabled()) {
                this.addSyncManagerListeners();
            }
        },

        removeManagerListeners: function () {
            if (this._managerListeners) {
                this.manager.removeSyncEventListener("syncChanged", "syncChangedObserver", this);
                this._managerListeners = false;
            }
            this.removeSyncManagerListeners();
        },

        addSyncManagerListeners: function () {
            if (!this._syncManagerListeners) {
                this.manager.addSyncEventListener("rootSet", "rootSetObserver", this);
                this.manager.addSyncEventListener("rootDestroyed", "rootDestroyedObserver", this);
                this.manager.addSyncEventListener("childMoved", "childMovedObserver", this);

                if (this.manager.hasRoot()) {
                    this.addNodeListenersRecursive(this.manager.getRoot());
                }

                this._syncManagerListeners = true;
            }
        },

        removeSyncManagerListeners: function () {
            if (this._syncManagerListeners) {
                this.manager.removeSyncEventListener("rootSet", "rootSetObserver", this);
                this.manager.removeSyncEventListener(
                    "rootDestroyed", "rootDestroyedObserver", this);
                this.manager.removeSyncEventListener("childMoved", "childMovedObserver", this);

                if (this.manager.hasRoot()) {
                    this.removeNodeListenersRecursive(this.manager.getRoot());
                }

                this._syncManagerListeners = false;
            }
        },

        nodeEvents: ["childAdded", "subTreeDestroyed", "childDetached", "childReattached",
            "propertiesSet", "propertiesUnset", "peerEvent", "partialUpdate"],


        addNodeListeners: function (node) {
            // use sync event listener so that we get the events first
            this.nodeEvents.forEach(function (eventType) {
                node.addSyncEventListener(eventType, eventType + "Observer", this);
            }, this);
        },

        removeNodeListeners: function (node) {
            // use sync event listener so that we get the events first
            this.nodeEvents.forEach(function (eventType) {
                node.removeSyncEventListener(eventType, eventType + "Observer", this);
            }, this);
        },

        addNodeListenersRecursive: function (node) {
            this.addNodeListeners(node);
            var i;
            var children = node.getChildren();
            for (i = 0; i < children.length; i += 1) {
                this.addNodeListenersRecursive(children[i]);
            }
        },

        removeNodeListenersRecursive: function (node) {
            this.removeNodeListeners(node);
            var i;
            var children = node.getChildren();
            for (i = 0; i < children.length; i += 1) {
                this.removeNodeListenersRecursive(children[i]);
            }
        }
    });

    PeerSynchronizer.debugNamespace = "/change this to debug something";
    return PeerSynchronizer;
});

